/** Онбординг-сторис: 5 экранов-«сторис» по функциям приложения
 *  (design_handoff_onboarding/README.md). Показывается один раз при первом
 *  открытии (гейт IntroGate, фичефлаг `intro`), повторно — из настроек
 *  («Как это работает» → роут /intro, IntroScreen). */
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useFlags, useMarkIntroSeen, useProfile } from '../../api/hooks';
import { track } from '../../analytics';
import { setCelebrationPending } from '../../celebrationGate';
import { backButton, haptics } from '../../telegram';
import { CalendarIcon, ChartIcon, UserIcon } from '../icons';
import { ShieldCheckIcon, TicketStarIcon } from './icons';
import {
  VisualAchievements,
  VisualPass,
  VisualProfile,
  VisualRating,
  VisualSchedule,
} from './visuals';

const SEEN_KEY = 'straddle_intro_seen';

function localSeen(): boolean {
  try {
    return localStorage.getItem(SEEN_KEY) === '1';
  } catch {
    return false;
  }
}

function markLocalSeen() {
  try {
    localStorage.setItem(SEEN_KEY, '1');
  } catch {
    /* приватный режим — переживём, бэк всё равно помнит */
  }
}

type CloseVia = 'skip' | 'cta' | 'back';

const SCREENS = [
  {
    Icon: CalendarIcon,
    title: 'Расписание клуба',
    text: 'Все турниры на неделю вперёд. Выбирай вечер и записывайся в один тап.',
    Visual: VisualSchedule,
  },
  {
    Icon: UserIcon,
    title: 'Это ты',
    text: 'Здесь копится твоя статистика и история игр. Заполнять ничего не нужно.',
    Visual: VisualProfile,
  },
  {
    Icon: ChartIcon,
    title: 'Два рейтинга',
    text: 'Личный показывает, как ты играешь. Сезонный показывает твоё место в клубе в этом сезоне.',
    Visual: VisualRating,
  },
  {
    Icon: ShieldCheckIcon,
    title: 'Играешь больше, платишь меньше',
    text: 'Достижения дают очки, очки поднимают уровень лояльности — и скидку на всё в клубе.',
    Visual: VisualAchievements,
  },
  {
    Icon: TicketStarIcon,
    title: 'Straddle Pass',
    text: 'Сезонный пропуск клуба: играй, выполняй задания, поднимай уровень и забирай награды.',
    Visual: VisualPass,
  },
] as const;

/** Каркас сторис (общий для первого показа и повтора из настроек). */
function IntroShell({
  replay,
  onClose,
}: {
  replay: boolean;
  onClose: (via: CloseVia, step: number) => void;
}) {
  const [step, setStep] = useState(1);
  const [dir, setDir] = useState<1 | -1>(1);
  // Первый экран — без анимации появления (хэндофф §Анимация).
  const animatedRef = useRef(false);

  useEffect(() => {
    track('intro_open', { replay });
  }, [replay]);

  const go = useCallback(
    (d: 1 | -1) => {
      const next = step + d;
      if (next < 1 || next > SCREENS.length) return; // без «отскока» на границах
      haptics.light();
      animatedRef.current = true;
      setDir(d);
      setStep(next);
    },
    [step],
  );

  // Telegram BackButton: шаг назад; на первом экране виден только в replay
  // (закрыть и вернуться в настройки).
  const stepRef = useRef(step);
  stepRef.current = step;
  const showBack = replay || step > 1;
  useEffect(() => {
    if (!showBack) return;
    const handler = () => {
      if (stepRef.current > 1) {
        haptics.light();
        animatedRef.current = true;
        setDir(-1);
        setStep((s) => Math.max(1, s - 1));
      } else {
        onClose('back', stepRef.current);
      }
    };
    backButton.show(handler);
    return () => backButton.hide(handler);
  }, [showBack, onClose]);

  // Клавиатура (веб): ← → листают, Backspace — назад.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') go(1);
      else if (e.key === 'ArrowLeft' || e.key === 'Backspace') go(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [go]);

  // Тап по половинам экрана (<400мс, не по кнопкам) + свайп (|dx| > 40px).
  const down = useRef<{ x: number; t: number } | null>(null);
  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    down.current = { x: e.clientX, t: Date.now() };
  };
  const onPointerUp = (e: ReactPointerEvent<HTMLDivElement>) => {
    const d = down.current;
    down.current = null;
    if (!d) return;
    if ((e.target as HTMLElement).closest('button')) return;
    const dx = e.clientX - d.x;
    if (Math.abs(dx) > 40) {
      go(dx < 0 ? 1 : -1);
      return;
    }
    if (Date.now() - d.t >= 400) return;
    const rect = e.currentTarget.getBoundingClientRect();
    go(e.clientX < rect.left + rect.width / 2 ? -1 : 1);
  };

  const { Icon, title, text, Visual } = SCREENS[step - 1];
  const last = step === SCREENS.length;
  const animCls = animatedRef.current ? (dir === 1 ? ' intro-fwd' : ' intro-bwd') : '';

  return (
    <div className="intro" onPointerDown={onPointerDown} onPointerUp={onPointerUp}>
      <div className="intro-shell">
        <div className="intro-progress" aria-hidden>
          {SCREENS.map((_, i) => (
            <i key={i} className={i < step ? 'on' : ''} />
          ))}
        </div>
        <div className="intro-skip-row">
          <button
            type="button"
            className="intro-skip"
            style={last ? { visibility: 'hidden' } : undefined}
            onClick={() => {
              haptics.tap();
              onClose('skip', step);
            }}
          >
            Пропустить
          </button>
        </div>
        <VisualSlot step={step} animCls={animCls}>
          <Visual />
        </VisualSlot>
        <div className={`intro-text${animCls}`} key={`t${step}`}>
          <span className="intro-ico">
            <Icon />
          </span>
          <h1>{title}</h1>
          <p>{text}</p>
        </div>
        <div className="intro-cta-slot">
          <button
            type="button"
            className="intro-cta"
            style={last ? undefined : { visibility: 'hidden' }}
            onClick={() => {
              haptics.tap();
              onClose('cta', step);
            }}
          >
            Выбрать игру
          </button>
        </div>
      </div>
    </div>
  );
}

/** Фрагмент интерфейса 358px: абсолютное центрирование + downscale по слоту. */
function VisualSlot({
  step,
  animCls,
  children,
}: {
  step: number;
  animCls: string;
  children: ReactNode;
}) {
  const slotRef = useRef<HTMLDivElement>(null);
  const fragRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  useLayoutEffect(() => {
    const slot = slotRef.current;
    const frag = fragRef.current;
    if (!slot || !frag) return;
    const measure = () => {
      const fh = frag.offsetHeight || 1;
      setScale(Math.min(1, slot.clientWidth / 358, slot.clientHeight / fh));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(slot);
    ro.observe(frag);
    return () => ro.disconnect();
  }, [step]);

  return (
    <div className="intro-slot" ref={slotRef}>
      <div className="intro-frag" ref={fragRef} style={{ transform: `scale(${scale})` }}>
        <div className={`intro-frag-anim${animCls}`} key={`v${step}`}>
          {children}
        </div>
      </div>
    </div>
  );
}

/** Первый показ: оверлей в App, виден пока флаг intro включён и профиль
 *  ещё не отмечен (бэк introSeen=false, локального дубля нет). */
export function IntroGate({ enabled }: { enabled: boolean }) {
  const navigate = useNavigate();
  const flags = useFlags();
  const flagOn = Boolean(flags.data?.intro);
  const [dismissed, setDismissed] = useState(false);
  const seenLocal = useMemo(localSeen, []);
  const want = enabled && flagOn && !seenLocal && !dismissed;
  const profile = useProfile(null, want);
  const visible = want && profile.data?.introSeen === false;

  // Прочие празднования (ачивки/рекапы) ждут, пока сторис не закрыт.
  useEffect(() => {
    setCelebrationPending('intro', visible);
    return () => setCelebrationPending('intro', false);
  }, [visible]);

  const mark = useMarkIntroSeen();
  const close = useCallback(
    (via: CloseVia, step: number) => {
      markLocalSeen();
      mark.mutate(); // fire-and-forget: вотермарк на бэке
      track('intro_close', { via, step, replay: false });
      setDismissed(true);
      navigate('/schedule', { replace: true });
    },
    [mark, navigate],
  );

  if (!visible) return null;
  return <IntroShell replay={false} onClose={close} />;
}

/** Повторный показ из настроек («Как это работает»): роут /intro.
 *  Отметку «видел» не трогает. */
export function IntroScreen() {
  const navigate = useNavigate();
  const close = useCallback(
    (via: CloseVia, step: number) => {
      track('intro_close', { via, step, replay: true });
      if (via === 'cta') navigate('/schedule');
      else navigate(-1);
    },
    [navigate],
  );
  return <IntroShell replay onClose={close} />;
}
